import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'button',
    'popover',
    'searchView',
    'chatView',
    'messagesContainer',
    'chatInput',
    'searchInput',
    'form',
  ];

  connect() {
    const startOpen = this.popoverTarget.dataset.startOpen === 'true';

    this.isOpen = startOpen;

    if (startOpen) {
      this.positionPopover();
    }

    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
  }

  togglePopover(event) {
    event.stopPropagation();
    this.isOpen ? this.closePopover() : this.openPopover();
  }

  openPopover() {
    this.popoverTarget.classList.remove('tw-hidden');
    this.isOpen = true;
    this.positionPopover();
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.add('tw-hidden');
      this.tooltipTarget.style.display = 'none';
    }
    // Hide the button when popover opens
    this.buttonTarget.style.visibility = 'hidden';
  }

  closePopover() {
    this.popoverTarget.classList.add('tw-hidden');
    this.isOpen = false;
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.remove('tw-hidden');
      this.tooltipTarget.style.display = 'block';
    }
    // Show the button when popover closes
    this.buttonTarget.style.visibility = 'visible';
  }

  handleOutsideClick(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.closePopover();
    }
  }

  positionPopover() {
    const button = this.buttonTarget;
    const popover = this.popoverTarget;
    const placement = popover.dataset.placement || 'top-end';

    // Force browser reflow to get correct dimensions
    void popover.offsetHeight;

    const buttonRect = button.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    let top = 0;
    let left = 0;
    const gap = 16;

    switch (placement) {
      case 'top-end':
        top = -(popoverRect.height - buttonRect.height);
        left = -(popoverRect.width - buttonRect.width);
        break;
      case 'top-start':
        top = -(popoverRect.height - buttonRect.height);
        left = 0;
        break;
      case 'bottom-end':
        top = buttonRect.height + gap;
        left = -(popoverRect.width - buttonRect.width);
        break;
      case 'bottom-start':
        top = buttonRect.height + gap;
        left = 0;
        break;
    }

    const finalLeft = buttonRect.left + left;

    if (finalLeft < 0) {
      left = -buttonRect.left + 16;
    } else if (finalLeft + popoverRect.width > window.innerWidth) {
      left = window.innerWidth - buttonRect.left - popoverRect.width - 32;
    }

    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
  }

  selectSuggestion(event) {
    const suggestion = event.currentTarget.dataset.suggestion;
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = suggestion;
      this.performSearch(suggestion);
    }
  }

  handleSearchSubmit(event) {
    if (event?.type === 'keydown' && event.key !== 'Enter') return;
    event?.preventDefault();

    const query = this.searchInputTarget?.value.trim();
    if (query) {
      this.performSearch(query);
    }
  }

  performSearch(query) {
    if (!query?.trim()) return;

    this.showChatView();

    // Set the input value and submit the form
    this.chatInputTarget.value = query;
    this.formTarget.requestSubmit();
    this.chatInputTarget.value = '';
  }

  showChatView() {
    this.searchViewTarget?.classList.add('tw-hidden');
    if (this.hasChatViewTarget) {
      this.chatViewTarget.classList.remove('tw-hidden');
      this.chatViewTarget.classList.add('tw-flex');
    }
    this.positionPopover();
  }

  closeChatView() {
    this.chatViewTarget?.classList.add('tw-hidden');
    this.chatViewTarget?.classList.remove('tw-flex');
    this.searchViewTarget?.classList.remove('tw-hidden');
    this.closePopover();
  }

  sendMessage(event) {
    if (event?.type === 'keydown' && event.key !== 'Enter') return;
    event.preventDefault();

    const message = this.chatInputTarget?.value.trim();
    if (!message) return;

    this.formTarget.requestSubmit();
    this.chatInputTarget.value = '';
  }
}
